import React, { useState, useEffect, useMemo } from 'react';
import {
  ConfigProvider,
  Form,
  Modal,
  Button,
  Radio,
  message,
  Typography,
  Select,
  Slider,
  Row,
  Col,
  InputNumber,
} from 'antd';
// import { useNavigate, useLocation } from "react-router-dom";
import {
  UserInfo,
  GoalType,
  AllocationType,
  PortfolioState,
  Portfolio,
  entries,
  GOALS,
  ALLOCATIONS,
  RETIREMENT_TYPES,
} from '@/common/widgets/portfolio/util';
import {
  getValueFromEvent,
  getValueProps,
  numberFormatter,
  numberParser,
} from '@wavvest/shared-utils';
// import { useFormModal } from "@/common/components/modal/context";
import {
  editInvestorFinancialPlans,
  generateIncomeExpenseProjection,
  generateProjectedSaving,
  generateHomePriceProjection,
  CalculateProbabilityOfSuccess,
  InvestorProfile,
} from '@/api/custom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  // setInvestors,
  selectInvestors,
  selectInvestorsLoadingState,
} from '@/store/features/investors/investorsSlice';
import './index.scss';
import { fetchInvestorsAllThunk } from '@/store/features/investors/investorsThunk';

const { Title } = Typography;
const { Option } = Select;

const initialPortfolios: Record<GoalType, Portfolio> = {
  'Just Invest': {
    totalInvested: 100000,
    allocations: {
      Growth: 60,
      'Capital Preservation': 30,
      'Money Market': 10,
    },
    timeHorizon: 10,
  },
  Retirement: {
    totalInvested: 100000,
    allocations: {
      Growth: 50,
      'Capital Preservation': 40,
      'Money Market': 10,
    },
    timeHorizon: 20,
    retirementTypes: ['ROTH', 'TRADITIONAL', 'ROLLOVER'],
  },
  'Major Purchase': {
    totalInvested: 100000,
    allocations: {
      Growth: 70,
      'Capital Preservation': 20,
      'Money Market': 10,
    },
    timeHorizon: 5,
  },
  'Supplement Income': {
    totalInvested: 100000,
    allocations: {
      Growth: 40,
      'Capital Preservation': 50,
      'Money Market': 10,
    },
    timeHorizon: 15,
  },
};

type FinancialPlanDetailProps = {
  plan: PortfolioState | null;
  user: InvestorProfile | null;
  onBack: () => void;
};

const FinancialPlanEdit: React.FC<FinancialPlanDetailProps> = ({ plan, user, onBack }) => {
  const [selectedUser, setSelectedUser] = useState<string>('');
  // const { showModal } = useFormModal();
  const dispatch = useAppDispatch();
  const investors = useAppSelector(selectInvestors);
  const investorsLoadingState = useAppSelector(selectInvestorsLoadingState);
  const loading = investorsLoadingState === 'pending';

  const [form] = Form.useForm();

  // const navigate = useNavigate();
  const [hasHouse, setHasHouse] = useState<boolean>(false);
  const [portfolios, setPortfolios] = useState<Partial<Record<GoalType, Portfolio>>>({});
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const enabledPortfolios: [GoalType, Portfolio][] = useMemo(() => {
    return entries(portfolios).filter(
      ([, portfolio]) => portfolio !== undefined && portfolio.timeHorizon > 0,
    ) as [GoalType, Portfolio][];
  }, [portfolios]);

  const selectedPortfolios = useMemo(() => {
    return enabledPortfolios.map(([portfolioName]) => portfolioName);
  }, [enabledPortfolios]);

  useEffect(() => {
    if (plan) {
      setPortfolios(plan.portfolios);
      form.setFieldsValue(plan);
      if (plan.userInfo.house) {
        setHasHouse(true);
      }
    }
  }, [plan, form]);

  useEffect(() => {
    dispatch(fetchInvestorsAllThunk());
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      if ('id' in user) {
        // handle investor user data
        // console.log("combinedData", combinedData);
        // const targetUser = combinedData.find(
        const targetUser = investors.find(
          (item) =>
            'id' in item && item.id === user.id,
        );
        if (targetUser) {
          setSelectedUser(
            //   : targetUser.id,
            targetUser.id,
          );
        }
        // } else {
        //   // handle name, email and date of birth search
        //   const targetUser = combinedData.find(
        //     (item) =>
        //       (item.name === user.name || item.email === user.email) &&
        //       item.date_of_birth === user.date_of_birth,
        //   );
        //   if (targetUser) {
        //     setSelectedUser(
        //         : targetUser.id,
        //     );
        //   } else {
        //     message.error(`User ${user.name} not found`);
        //     setSelectedUser("");
        //   }
      }
    }
    // }, [combinedData, user]);
  }, [investors, user]);

  useEffect(() => {
    return () => {
      form.resetFields();
      setPortfolios({});
    };
  }, [form]);

  const handleUserChange = (value: string) => {
    // if (value === "new") {
    // } else {
    // const selectedUser = combinedData.find((data) =>
    const selectedUser = investors.find(
      (data) =>
        //   : data.id === value,
        data.id === value,
    );
    if (selectedUser) {
      setSelectedUser(
        //   : selectedUser.id,
        selectedUser.id,
      );
    }
    // }
  };

  interface FormValues {
    userInfo: UserInfo;
    portfolios: Partial<Record<GoalType, Portfolio>>;
  }

  const adjustAllocations = (growth: number, capitalPreservation: number) => {
    const adjustedTotal = growth + capitalPreservation;
    if (adjustedTotal !== 100) {
      const diff = 100 - adjustedTotal;
      growth += diff;
    }
    return { growth, capitalPreservation };
  };

  const handleConfirm = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue() as FormValues;

      const portfolioState: PortfolioState = {
        userInfo: {
          retirementAge: values.userInfo.retirementAge,
          longevity: values.userInfo.longevity,
          married: values.userInfo.married,
          annualIncome: values.userInfo.annualIncome,
          monthlyExpenses: values.userInfo.monthlyExpenses,
          totalSavings: values.userInfo.totalSavings,
          liquidNetWorth: values.userInfo.liquidNetWorth,
          annualWithdrawal: values.userInfo.annualWithdrawal,
          house: values.userInfo.house,
          houseValue: values.userInfo.house ? values.userInfo.houseValue : undefined,
          mortgageRemaining: values.userInfo.house ? values.userInfo.mortgageRemaining : undefined,
        },
        portfolios: values.portfolios,
        scenarioTimestamp: new Date().toISOString(),
      };

      // const createdUser = user && "id" in user ?
      //   combinedData.find(data => data.id === user.id) :
      //   combinedData.find(data => data.id === selectedUser);

      // const createdUser = combinedData.find((data) =>
      //     : data.id === selectedUser,
      // );
      const createdUser = investors.find((data) => data.id === selectedUser);

      if (createdUser && createdUser.date_of_birth) {
        const birthDate = new Date(createdUser.date_of_birth);
        const age = createdUser.date_of_birth
          ? new Date().getFullYear() - birthDate.getFullYear()
          : 30;
        setSubmitLoading(true);
        try {
          for (const goal of GOALS) {
            const portfolio = values.portfolios[goal];
            if (portfolio === undefined) {
              // Ignoring unselected portfolios
              continue;
            }

            // Exception cases
            if (goal === 'Supplement Income') {
              for (const allocation of ALLOCATIONS) {
                portfolio.allocations[allocation] = undefined;
              }
              portfolio.allocations['Yield'] = 100;
            } else if (goal === 'Major Purchase' && portfolio.timeHorizon <= 2) {
              for (const allocation of ALLOCATIONS) {
                portfolio.allocations[allocation] = undefined;
              }
              portfolio.allocations['Money Market'] = 100;
            } else {
              // General case
              for (const allocation of ALLOCATIONS) {
                portfolio.allocations[allocation] = 0;
              }
              portfolio.allocations['Yield'] = undefined;
              portfolio.allocations['Money Market'] = undefined;
              if (portfolio.timeHorizon >= 10) {
                portfolio.allocations['Growth']! += 33;
              } else {
                portfolio.allocations['Growth']! += 11;
                portfolio.allocations['Capital Preservation']! += 22;
              }

              const { growth, capitalPreservation } = adjustAllocations(
                portfolio.allocations['Growth']!,
                portfolio.allocations['Capital Preservation']!,
              );

              portfolio.allocations['Growth'] = growth;
              portfolio.allocations['Capital Preservation'] = capitalPreservation;
            }
          }

          portfolioState.portfolios = values.portfolios;

          // const userId =
          //     : createdUser.id;
          const userId = createdUser.id;

          const portfoliosArray = Object.values(values.portfolios)
            .filter((portfolio): portfolio is Portfolio => portfolio !== undefined)
            .map((portfolio) => ({
              totalInvested: portfolio.totalInvested,
              allocations: portfolio.allocations,
              timeHorizon: portfolio.timeHorizon,
            }));

          const [
            incomeExpenseProjectionData,
            projectedSavingData,
            homePriceProjectionData,
            probabilityOfSuccessData,
          ] = await Promise.all([
            generateIncomeExpenseProjection(userId, {
              initialIncome: values.userInfo.annualIncome,
              initialExpenses: values.userInfo.monthlyExpenses * 12,
              currentAge: age,
              retirementAge: values.userInfo.retirementAge,
              longevity: values.userInfo.longevity,
              initialSavings: values.userInfo.totalSavings,
            }),
            generateProjectedSaving(userId, {
              initial_income: values.userInfo.annualIncome,
              initial_expenses: values.userInfo.monthlyExpenses * 12,
              current_age: age,
              retirement_age: values.userInfo.retirementAge,
              longevity: values.userInfo.longevity,
              initial_savings: values.userInfo.totalSavings,
              portfolios: portfoliosArray,
            }),
            generateHomePriceProjection(userId, {
              currentValue: values.userInfo.houseValue || 0,
              currentAge: age,
              longevity: values.userInfo.longevity,
            }),
            CalculateProbabilityOfSuccess(userId, {
              annualIncome: values.userInfo.annualIncome,
              annualExpense: values.userInfo.monthlyExpenses * 12,
              currentSavings: values.userInfo.totalSavings,
              currentAge: age,
              retirementAge: values.userInfo.retirementAge,
              longevity: values.userInfo.longevity,
              annualWithdrawal: values.userInfo.annualWithdrawal,
              portfolios: portfoliosArray,
            }),
          ]);

          portfolioState.incomeExpenseProjection = incomeExpenseProjectionData;
          portfolioState.projectionSavings = projectedSavingData;
          portfolioState.homePriceProjection = homePriceProjectionData;
          portfolioState.probabilityOfSuccess = probabilityOfSuccessData.probability_of_success;

          await editInvestorFinancialPlans(userId, portfolioState);
          message.success('Financial plan confirmed successfully!');
          onBack();
          // navigate("/home/investor-financial-plan", {
          //   state: { user: { id: userId } },
          // });
        } catch (error) {
          console.error('Error confirming financial plan:', error);
          message.error('Failed to create financial plan');
        } finally {
          setSubmitLoading(false);
        }
      } else {
        message.error('No user selected');
      }
    } catch (e) {
      Modal.warning({
        title: 'Some fields in the form need your attention.',
        content: (
          <div style={{ fontSize: '16px', lineHeight: '1.5' }}>
            <p>Please check the highlighted fields, and update your answers.</p>
          </div>
        ),
        maskClosable: true,
        centered: true,
        footer: null,
      });
    }
  };

  const handlePortfolioClick = (goal: GoalType) => {
    if (selectedPortfolios.includes(goal)) {
      const updatedPortfolios = { ...portfolios };
      delete updatedPortfolios[goal];
      setPortfolios(updatedPortfolios);
    } else {
      if (initialPortfolios[goal]) {
        setPortfolios({
          ...portfolios,
          [goal]: initialPortfolios[goal],
        });
      }
    }
  };

  const handleSliderChange = (
    portfolioName: GoalType,
    allocationType: AllocationType,
    value: number,
  ) => {
    const currentAllocations = form.getFieldValue(['portfolios', portfolioName, 'allocations']);
    const otherAllocations = Object.keys(currentAllocations).filter(
      (key) => key !== allocationType,
    ) as AllocationType[];
    const totalOtherValues = otherAllocations.reduce(
      (acc, key) => acc + currentAllocations[key],
      0,
    );

    if (totalOtherValues + value <= 100) {
      const newAllocations = {
        ...currentAllocations,
        [allocationType]: value,
      };

      form.setFieldsValue({
        portfolios: {
          [portfolioName]: {
            allocations: newAllocations,
          },
        },
      });
    } else {
      const maxAllowedValue = 100 - totalOtherValues;
      const newAllocations = {
        ...currentAllocations,
        [allocationType]: maxAllowedValue,
      };

      form.setFieldsValue({
        portfolios: {
          [portfolioName]: {
            allocations: newAllocations,
          },
        },
      });
    }
  };

  return (
    <div className="edit-plan">
      <ConfigProvider
        theme={{
          components: {
            Slider: {
              handleActiveColor: 'rgb(114,65,137)',
              handleActiveOutlineColor: 'rgb(114,65,137)',
              handleColor: 'rgb(80,41,151)',
              trackBg: 'rgb(114,65,137)',
              railBg: 'rgb(171,171,171)',
              trackHoverBg: 'rgb(114,65,137)',
              colorText: 'rgb(255,255,255)',
              colorTextDescription: 'rgb(114,65,137)',
            },
            Tooltip: {
              colorBgSpotlight: '#724189',
              colorText: '#FFFFFF',
            },
          },
        }}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              {user && 'id' in user ? (
                <Form.Item label="Current User">{user.name + ' : ' + user.email}</Form.Item>
              ) : (
                <Form.Item name="user" label="Select User">
                  <Select
                    placeholder="Select a client"
                    style={{ width: '100%' }}
                    value={selectedUser}
                    onChange={handleUserChange}
                    loading={loading}
                  >
                    {/* <Option value="Assigned Investors" disabled>
                      --- Assigned Investors ---
                    </Option>
                    {assignedInvestors.map((investor) => (
                      
                      <Option
                      >
                        {investor.name} ({investor.email})
                      </Option>
                    ))}
                    
                    <Option value="Unassigned Investors" disabled>
                      --- Unassigned Investors ---
                    </Option>
                    {unassignedInvestors.map((investor) => (
                      <Option
                      >
                        {investor.name} ({investor.email}) [Unassigned]
                      </Option>
                    ))} */}

                    {investors.map((investor) => (
                      <Option key={investor.id} value={investor.id}>
                        {investor.name} ({investor.email})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Retirement Age"
                name={['userInfo', 'retirementAge']}
                rules={[
                  {
                    required: true,
                    message: 'Please input your retirement age!',
                  },
                ]}
                getValueFromEvent={getValueFromEvent}
                getValueProps={getValueProps}
              >
                <InputNumber
                  size={'large'}
                  style={{ width: '100%' }}
                  min={0}
                  disabled={!selectedUser}
                  controls={false}
                  formatter={numberFormatter}
                  parser={numberParser}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Longevity"
                name={['userInfo', 'longevity']}
                rules={[{ required: true, message: 'Please input your longevity!' }]}
                getValueFromEvent={getValueFromEvent}
                getValueProps={getValueProps}
              >
                <InputNumber
                  size={'large'}
                  style={{ width: '100%' }}
                  min={0}
                  disabled={!selectedUser}
                  controls={false}
                  formatter={numberFormatter}
                  parser={numberParser}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Annual Income (USD)"
                name={['userInfo', 'annualIncome']}
                rules={[
                  {
                    required: true,
                    message: 'Please input your annual income!',
                  },
                ]}
                getValueFromEvent={getValueFromEvent}
                getValueProps={getValueProps}
              >
                <InputNumber
                  size={'large'}
                  style={{ width: '100%' }}
                  min={0}
                  controls={false}
                  formatter={numberFormatter}
                  parser={numberParser}
                  disabled={!selectedUser}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Monthly Expenses (USD)"
                name={['userInfo', 'monthlyExpenses']}
                rules={[
                  {
                    required: true,
                    message: 'Please input your monthly expenses!',
                  },
                ]}
                getValueFromEvent={getValueFromEvent}
                getValueProps={getValueProps}
              >
                <InputNumber
                  size={'large'}
                  style={{ width: '100%' }}
                  min={0}
                  controls={false}
                  formatter={numberFormatter}
                  parser={numberParser}
                  disabled={!selectedUser}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Total Savings (USD)"
                name={['userInfo', 'totalSavings']}
                rules={[
                  {
                    required: true,
                    message: 'Please input your total savings!',
                  },
                ]}
                getValueFromEvent={getValueFromEvent}
                getValueProps={getValueProps}
                style={{ whiteSpace: 'nowrap' }}
              >
                <InputNumber
                  size={'large'}
                  style={{ width: '100%' }}
                  min={0}
                  controls={false}
                  formatter={numberFormatter}
                  parser={numberParser}
                  disabled={!selectedUser}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Liquid Net Worth (USD)"
                name={['userInfo', 'liquidNetWorth']}
                rules={[
                  {
                    required: true,
                    message: 'Please input your liquid net worth!',
                  },
                ]}
                getValueFromEvent={getValueFromEvent}
                getValueProps={getValueProps}
                style={{ whiteSpace: 'nowrap' }}
              >
                <InputNumber
                  size={'large'}
                  style={{ width: '100%' }}
                  min={0}
                  controls={false}
                  formatter={numberFormatter}
                  parser={numberParser}
                  disabled={!selectedUser}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Annual Withdrawal (USD)"
                name={['userInfo', 'annualWithdrawal']}
                rules={[
                  {
                    required: true,
                    message: 'Please input your annual withdrawal!',
                  },
                ]}
                getValueFromEvent={getValueFromEvent}
                getValueProps={getValueProps}
                style={{ whiteSpace: 'nowrap' }}
              >
                <InputNumber
                  size={'large'}
                  style={{ width: '100%' }}
                  min={0}
                  controls={false}
                  formatter={numberFormatter}
                  parser={numberParser}
                  disabled={!selectedUser}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="What is your marital status?"
                name={['userInfo', 'married']}
                wrapperCol={{ span: 12 }}
                rules={[
                  {
                    required: true,
                    message: 'Please select your marital status!',
                  },
                ]}
              >
                <Radio.Group
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '16px',
                  }}
                  disabled={!selectedUser}
                >
                  <Radio value={true}>Married</Radio>
                  <Radio value={false}>Single</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Do you own a house?"
                name={['userInfo', 'house']}
                wrapperCol={{ span: 12 }}
                rules={[
                  {
                    required: true,
                    message: 'Please select if you own a house!',
                  },
                ]}
              >
                <Radio.Group
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '16px',
                  }}
                  disabled={!selectedUser}
                  onChange={(e) => setHasHouse(e.target.value)}
                >
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          {hasHouse && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="House Value"
                  name={['userInfo', 'houseValue']}
                  rules={[
                    {
                      required: true,
                      message: 'Please input your house value!',
                    },
                  ]}
                  getValueFromEvent={getValueFromEvent}
                  getValueProps={getValueProps}
                >
                  <InputNumber
                    size={'large'}
                    style={{ width: '100%' }}
                    min={0}
                    controls={false}
                    formatter={numberFormatter}
                    parser={numberParser}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Mortgage Remaining Value"
                  name={['userInfo', 'mortgageRemaining']}
                  rules={[
                    {
                      required: true,
                      message: 'Please input your mortgage remaining value!',
                    },
                  ]}
                  getValueFromEvent={getValueFromEvent}
                  getValueProps={getValueProps}
                >
                  <InputNumber
                    size={'large'}
                    style={{ width: '100%' }}
                    min={0}
                    controls={false}
                    formatter={numberFormatter}
                    parser={numberParser}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Form.Item label="Add portfolio">
            <Row gutter={16}>
              {GOALS.map((goal) => (
                <Col key={goal}>
                  <div
                    className={`portfolio-item ${selectedPortfolios.includes(goal) ? 'selected' : ''} ${!selectedUser && 'disabled'}`}
                    onClick={() => {
                      if (selectedUser) {
                        handlePortfolioClick(goal);
                      }
                    }}
                  >
                    {goal}
                  </div>
                </Col>
              ))}
            </Row>
          </Form.Item>

          <div className="portfolio-container">
            {enabledPortfolios.map(([portfolioName, portfolioData]: [GoalType, Portfolio]) => (
              <div key={portfolioName} className={`portfolio-content`}>
                <Title level={3}>{portfolioName}</Title>

                <Form.Item
                  label="Total Invested"
                  name={['portfolios', portfolioName, 'totalInvested']}
                  rules={[
                    {
                      required: true,
                      message: 'Please input the total invested amount!',
                    },
                  ]}
                  getValueFromEvent={getValueFromEvent}
                  getValueProps={getValueProps}
                >
                  <InputNumber
                    size={'large'}
                    style={{ width: '100%' }}
                    min={0}
                    controls={false}
                    formatter={numberFormatter}
                    parser={numberParser}
                  />
                </Form.Item>

                {portfolioName === 'Supplement Income' && (
                  <Form.Item
                    label="Yield"
                    name={['portfolios', portfolioName, 'allocations', 'Yield']}
                    rules={[
                      {
                        required: true,
                        message: 'Please input the Yield allocation!',
                      },
                    ]}
                  >
                    <Slider
                      min={0}
                      max={100}
                      marks={{ 0: '0%', 100: '100%' }}
                      tooltip={{
                        formatter: (value) => `${value}%`,
                      }}
                      value={form.getFieldValue([
                        'portfolios',
                        portfolioName,
                        'allocations',
                        'Yield',
                      ])}
                      onChange={(value) => handleSliderChange(portfolioName, 'Yield', value)}
                    />
                  </Form.Item>
                )}

                {portfolioName !== 'Supplement Income' && (
                  <Form.Item
                    label="Growth"
                    name={['portfolios', portfolioName, 'allocations', 'Growth']}
                    rules={[
                      {
                        required: true,
                        message: 'Please input the Growth allocation!',
                      },
                    ]}
                  >
                    <Slider
                      min={0}
                      max={100}
                      marks={{ 0: '0%', 100: '100%' }}
                      tooltip={{
                        formatter: (value) => `${value}%`,
                      }}
                      value={form.getFieldValue([
                        'portfolios',
                        portfolioName,
                        'allocations',
                        'Growth',
                      ])}
                      onChange={(value) => handleSliderChange(portfolioName, 'Growth', value)}
                    />
                  </Form.Item>
                )}

                {portfolioName !== 'Supplement Income' && (
                  <Form.Item
                    label="Capital Preservation"
                    name={['portfolios', portfolioName, 'allocations', 'Capital Preservation']}
                    rules={[
                      {
                        required: true,
                        message: 'Please input the Capital Preservation allocation!',
                      },
                    ]}
                  >
                    <Slider
                      min={0}
                      max={100}
                      marks={{ 0: '0%', 100: '100%' }}
                      tooltip={{
                        formatter: (value) => `${value}%`,
                      }}
                      value={form.getFieldValue([
                        'portfolios',
                        portfolioName,
                        'allocations',
                        'Capital Preservation',
                      ])}
                      onChange={(value) =>
                        handleSliderChange(portfolioName, 'Capital Preservation', value)
                      }
                    />
                  </Form.Item>
                )}

                <Form.Item
                  label="Time Horizon"
                  name={['portfolios', portfolioName, 'timeHorizon']}
                  rules={[
                    {
                      required: true,
                      message: 'Please input the time horizon!',
                    },
                  ]}
                  getValueFromEvent={getValueFromEvent}
                  getValueProps={getValueProps}
                >
                  <InputNumber
                    size={'large'}
                    style={{ width: '100%' }}
                    min={0}
                    controls={false}
                    formatter={numberFormatter}
                    parser={numberParser}
                  />
                </Form.Item>

                {portfolioName === 'Retirement' && portfolioData.retirementTypes && (
                  <Form.Item
                    label="Retirement Types"
                    name={['portfolios', portfolioName, 'retirementTypes']}
                    rules={[
                      {
                        required: true,
                        message: 'Please select the retirement types!',
                      },
                    ]}
                  >
                    <Select mode="multiple" size={'large'} placeholder="Select retirement types">
                      {RETIREMENT_TYPES.map((type) => (
                        <Option key={type} value={type}>
                          {type}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </div>
            ))}
          </div>
          <div className="button-container">
            <Button
              type="primary"
              htmlType="submit"
              onClick={handleConfirm}
              loading={submitLoading}
              disabled={!selectedUser}
            >
              Submit Plan
            </Button>
            <Button type="default" htmlType="button" onClick={onBack}>
              Cancel
            </Button>
          </div>
        </Form>
      </ConfigProvider>
    </div>
  );
};

export default FinancialPlanEdit;
